// src/pages/warehouse/components/ArtikelLagerplaetze.tsx
// Grundartikel-Detail-Abschnitt "Lagerplätze" (Spec §7): Paletten-Liste (aelteste zuerst)
// + Zwei-Welten-Anzeige Grosslager ca. vs. Plattform/DR (§3.5) + "Platz zuweisen" (F1b).
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useArtikelPositionen, type ArtikelPosition } from '@/api/warehouse'
import { apiFetch } from '@/api/client'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { useAuth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { kartonAufschluesselung, kartonText, kartonWort } from './kartonAufschluesselung'
import { VorrangZelle, vorrangText, type VorrangModus } from './VorrangZelle'
import { LeiterPaletteInline } from './LeiterPaletteInline'
import { LeiterPaletteKorrektur } from './LeiterPaletteKorrektur'
import HandregalKorrekturFeld from '@/components/warehouse/HandregalKorrekturFeld'

export function ArtikelLagerplaetze({ merchantSku }: { merchantSku: string }) {
  const { warehouse } = useAuth()
  const q = useArtikelPositionen(warehouse.general ? merchantSku : null)
  const qc = useQueryClient()
  const [platz, setPlatz] = useState('')
  const [assignBusy, setAssignBusy] = useState(false)
  const [rollFilter, setRollFilter] = useState('all')
  const [vorrangBusy, setVorrangBusy] = useState(false)
  const [korrektur, setKorrektur] = useState<ArtikelPosition | null>(null)
  // RA-BI (Re-Audit Runde 1, Welle W2-BE): idempotency_key ist auf
  // /position/assign/ jetzt Pflicht (positionAssignSchema). EIN stabiler Key
  // pro Zuordnungsversuch — bleibt bei Fehlschlag/Retry UNVERAENDERT (Server
  // dedupliziert einen Netz-Retry), wird erst nach Erfolg neu gezogen.
  const assignKeyRef = useRef(newIdempotencyKey())
  if (!warehouse.general) return null
  if (q.isLoading) return <div className="text-sm text-muted p-2">Lade Lagerplätze…</div>
  const d = q.data
  if (!d) return null
  const s = d.summary
  const offene = d.positions.filter((p) => p.state === 'ausgepackt').length
  const netze = s.unit === 'm'
  const kartonSum = s.kartonSum ?? 0
  const geschaetzt = s.circaSum - kartonSum
  const summaryQuantity = netze && s.unknownCount > 0 && s.unknownCount === d.positions.length
    ? 'unbekannt'
    : `${s.isCirca ? 'ca. ' : ''}${s.total} ${s.unit}${netze && s.unknownCount > 0 ? ' bekannt' : ''}`
  const rollLengths = [...new Set(d.positions.flatMap(p => p.netzeRollLengthM === null ? [] : [p.netzeRollLengthM]))].sort((a, b) => a - b)
  const visiblePositions = d.positions.filter(p => !netze || rollFilter === 'all'
    || (rollFilter === 'unknown' ? p.netzeRollLengthM === null : p.netzeRollLengthM === Number(rollFilter)))
  // Stückware: je Palette Kartons à Inhalt, darunter die Summe (Netze haben ihre eigene Spalte R / Verpackung)
  const zeilen = visiblePositions.map((p) => ({ p, menge: p.state === 'verschlossen' ? p.qtyExact : p.qtyCirca,
    karton: netze ? null : kartonAufschluesselung(p) }))
  const summe = zeilen.reduce((acc, z) => ({
    kartons: acc.kartons + (z.karton && !z.karton.einzeln ? z.karton.kartons : 0),
    einzeln: acc.einzeln + (z.karton?.einzeln ? 1 : 0),
    stueck: acc.stueck + (z.menge ?? 0),
    ohneKarton: acc.ohneKarton + (z.karton ? 0 : 1),
    unbekannt: acc.unbekannt + (z.menge === null ? 1 : 0),
  }), { kartons: 0, einzeln: 0, stueck: 0, ohneKarton: 0, unbekannt: 0 })
  // Vorrang-Spalte: Leiter bedienen sie; alle anderen sehen nur eine gesetzte Markierung.
  const vorrangSpalte = warehouse.lead || d.positions.some((p) => p.vorrang)
  const packplatzSpalte = warehouse.lead || d.positions.some((p) => p.packplatz)
  const spalten = 7 + (vorrangSpalte ? 1 : 0) + (packplatzSpalte ? 1 : 0) + (warehouse.lead ? 1 : 0)

  async function packplatzAendern(positionId: number, setzen: boolean) {
    if (vorrangBusy) return
    setVorrangBusy(true)
    const name = (id: number) => d!.positions.find(p => p.id === id)?.paletteNo ?? `Position ${id}`
    try {
      const r = await apiFetch<{ ersetzt?: { placeId: number; positionId: number | null } | null }>(
        `/warehouse/position/${positionId}/packplatz/`, { method: setzen ? 'PUT' : 'DELETE' })
      const alt = r?.ersetzt
      const ersetzt = alt ? alt.positionId !== null ? name(alt.positionId)
        : d!.positions.find(p => p.placeId === alt.placeId)?.placeBarcode ?? `Platz ${alt.placeId}` : null
      // Packplatz gilt je Rollenlänge (0166): Netze nennen die Variante.
      const laenge = d!.positions.find(p => p.id === positionId)?.netzeRollLengthM
      const rolle = laenge != null ? ` · Rolle ${laenge} m` : ''
      toast.success(`${name(positionId)}${rolle}: Packplatz ${setzen ? 'gesetzt' : 'aufgehoben'}${ersetzt ? ` (statt ${ersetzt})` : ''}`)
      void q.refetch()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
    finally { setVorrangBusy(false) }
  }

  async function vorrangAendern(positionId: number, modus: VorrangModus | null) {
    if (vorrangBusy) return
    setVorrangBusy(true)
    const name = (id: number) => d!.positions.find((p) => p.id === id)?.paletteNo ?? `Position ${id}`
    try {
      if (modus) {
        const r = await apiFetch<{ ersetzt: { positionId: number } | null }>(`/warehouse/position/${positionId}/vorrang/`,
          { method: 'PUT', body: JSON.stringify({ modus }) })
        toast.success(`${name(positionId)}: ${vorrangText[modus]} nehmen${r?.ersetzt ? ` (statt ${name(r.ersetzt.positionId)})` : ''}`)
      } else {
        await apiFetch(`/warehouse/position/${positionId}/vorrang/`, { method: 'DELETE' })
        toast.success(`${name(positionId)}: Vorrang aufgehoben`)
      }
      void q.refetch()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
    finally { setVorrangBusy(false) }
  }

  async function onAssign() {
    if (assignBusy) return
    setAssignBusy(true)
    try {
      await apiFetch('/warehouse/position/assign/', {
        method: 'POST',
        body: JSON.stringify({ artikel: merchantSku, place_barcode: platz, idempotency_key: assignKeyRef.current }),
      })
      toast.success(`Platz ${platz} zugewiesen (ohne Menge — V1)`)
      assignKeyRef.current = newIdempotencyKey() // naechste Zuordnung braucht einen neuen Key
      setPlatz(''); void q.refetch()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
    finally { setAssignBusy(false) }
  }

  return (
    <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold">Lagerplätze (Großlager)</div>
        <div className="text-sm">
          <b>Großlager{netze ? ' (alle R)' : ''}: {summaryQuantity}</b>
          {netze ? (s.isCirca || s.unknownCount > 0) && <span className="text-muted"> ({s.exactSum} m exakt verschlossen + ca. {s.circaSum} m offen / in Bewegung{s.unknownCount > 0 ? ` + ${s.unknownCount}× unbekannt` : ''})</span>
            : (s.circaSum > 0 || s.unknownCount > 0) && <span className="text-muted"> ({s.exactSum} exakt verschlossen{kartonSum > 0 ? ` + ${kartonSum} aus Restkartons gerechnet` : ''}{geschaetzt > 0 ? ` + ca. ${geschaetzt} geschätzt` : ''}{s.unknownCount > 0 ? ` + ${s.unknownCount}× unbekannt` : ''})</span>}
          <span className="mx-2 text-muted">·</span>
          Plattform/DR: <b>{s.drQuantity ?? '—'} {s.unit}</b>
          <span className="ml-1 text-xs text-muted">(Plausibilitäts-Check, kein Abgleich)</span>
        </div>
      </div>
      {s.gesamt !== undefined && (
        <div className="rounded-lg border border-line bg-panel-2 p-3 text-sm space-y-1">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span>Lager 1: {s.lager1} {s.unit}</span>
            <span>Lager 2: {s.lager2} {s.unit}</span>
            {s.packstation?.aktuell && <span>Packstation: physisch {s.packstation.physisch} {s.unit} · frei {s.packstation.frei} {s.unit}</span>}
            <b>Gesamt: {s.gesamt} {s.unit}</b>
          </div>
          {(s.packstationHinweis === 'noch_nicht_gezaehlt') && <div className="text-muted">Packstation: noch nicht gezählt</div>}
          {(s.packstationHinweis === 'zaehler_nicht_aktuell' || s.packstation?.aktuell === false) && <div className="text-muted">Packstation: Zähler nicht aktuell</div>}
          {s.gesamtUnvollstaendig && <div className="text-amber-700 dark:text-amber-300">Gesamtbestand unvollständig: Mengen unbekannt oder keinem Lager zugeordnet.</div>}
          <div className="text-xs text-muted">Gesamt = Lager 1 + Lager 2 + Packstation frei. Angebrochene Paletten zählen mit ihren Restkartons. Packstation zählt nur mit aktuellem Zähler.</div>
        </div>
      )}
      {offene > 1 && (
        <div className="text-xs rounded bg-amber-100 text-amber-900 px-2 py-1 inline-block">
          ⚠ {offene} Paletten gleichzeitig ausgepackt — Eine-offene-Palette-Regel (§3.4)
        </div>
      )}
      {netze && (
        <div className="flex items-center flex-wrap gap-2 text-sm">
          <label htmlFor="netze-roll-filter" className="text-muted">Rollenlänge</label>
          <select id="netze-roll-filter" aria-label="Rollenlänge filtern" value={rollFilter}
            onChange={e => setRollFilter(e.target.value)}
            className="rounded-md border border-line bg-panel px-2 py-1 font-mono focus-visible:outline-2 focus-visible:outline-primary">
            <option value="all">Alle R</option>
            {rollLengths.map(r => <option key={r} value={r}>R{r}</option>)}
            {d.positions.some(p => p.netzeRollLengthM === null) && <option value="unknown">R unbekannt</option>}
          </select>
          <span className="text-xs text-muted">Filter für diese Liste · Gesamtbestand oben für alle R</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted"><tr>{vorrangSpalte && <th className="p-1.5">Vorrang</th>}{packplatzSpalte && <th className="p-1.5">Packplatz</th>}<th className="p-1.5">Palette</th><th className="p-1.5">Platz</th>{netze && <th className="p-1.5">R / Verpackung</th>}<th className="p-1.5">Status</th>{!netze && <th className="p-1.5">Kartons</th>}<th className="p-1.5">Menge</th><th className="p-1.5">First-In</th><th className="p-1.5">Zettel</th>{warehouse.lead && <th className="p-1.5">Korrektur</th>}</tr></thead>
          <tbody>
            {zeilen.map(({ p, karton }) => (
              <tr key={p.id} className={`border-t border-line${p.vorrang ? ' bg-primary/5' : ''}`}>
                {vorrangSpalte && <td className="p-1.5">
                  <VorrangZelle p={p} lead={warehouse.lead} busy={vorrangBusy}
                    onSetzen={(m) => void vorrangAendern(p.id, m)} onAufheben={() => void vorrangAendern(p.id, null)} />
                </td>}
                {packplatzSpalte && <td className="p-1.5">
                  {warehouse.lead && (p.packplatz || (p.placeType === 'palettenplatz' && p.placeId !== null
                    && (p.state === 'verschlossen' || p.state === 'ausgepackt'))) ? (
                    <button disabled={vorrangBusy} aria-label={`Packplatz für ${p.paletteNo ?? `Position ${p.id}`} ${p.packplatz ? 'aufheben' : 'setzen'}`}
                      title={p.packplatz ? `gesetzt${p.packplatz.setByName ? ` von ${p.packplatz.setByName}` : ''}, ${new Date(p.packplatz.setAt).toLocaleDateString('de-DE')}` : undefined}
                      onClick={() => void packplatzAendern(p.id, !p.packplatz)}
                      className="inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap rounded px-1.5 -ml-1.5 hover:bg-panel-2 focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50">
                      <span aria-hidden className={`size-2.5 rounded-sm ${p.packplatz ? 'bg-primary' : 'ring-[1.5px] ring-muted/70'}`} />
                      <span className={`text-xs ${p.packplatz ? 'font-medium text-ink' : 'text-muted'}`}>{p.packplatz ? 'Packplatz' : 'Setzen'}</span>
                    </button>
                  ) : p.packplatz ? <span className="text-xs font-medium text-ink">Packplatz</span> : null}
                </td>}
                <td className="p-1.5 font-mono text-xs whitespace-nowrap">{p.paletteNo ?? '—'}</td>
                <td className="p-1.5 font-mono whitespace-nowrap">{p.placeBarcode ?? (p.state === 'in_transit' ? 'in Bewegung' : '—')}</td>
                {netze && <td className="p-1.5 whitespace-nowrap">
                  <div className="font-mono font-medium">{p.netzeRollLengthM === null ? 'R unbekannt' : `R${p.netzeRollLengthM} · ${p.netzeRollLengthM} m/Rolle`}</div>
                  <div className="text-xs text-muted">{p.netzeRollsPerCarton === null ? 'Verpackung unbekannt'
                    : `${p.netzeRollsPerCarton} ${p.netzeRollsPerCarton === 1 ? 'Rolle' : 'Rollen'}/Karton`}</div>
                </td>}
                <td className="p-1.5">
                  {p.state === 'verschlossen' && <span className="text-green-700 font-medium">verschlossen</span>}
                  {p.state === 'ausgepackt' && <span className="text-amber-600 font-medium">ausgepackt</span>}
                  {p.state === 'in_transit' && <span className="text-blue-600">in Bewegung</span>}
                  {p.isMisch && <span className="ml-1 text-xs bg-amber-200 text-amber-900 px-1 rounded">MISCH</span>}
                </td>
                {!netze && <td className="p-1.5 whitespace-nowrap">
                  {karton ? <>
                    <span className="font-mono" title={karton.einzeln ? '1 St. je Karton' : karton.gerechnet ? 'aus Menge ÷ Stück je Karton gerechnet' : undefined}>{kartonText(karton)}</span>
                    {!karton.passt && <span className="ml-1.5 text-xs rounded bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 px-1"
                      title={`${karton.kartons} × ${karton.proKarton}${karton.rest ? ` + ${karton.rest}` : ''} = ${karton.kartons * karton.proKarton + karton.rest} St., Etikett: ${p.qtyExact} St.`}>≠ Menge</span>}
                    {!karton.einzeln && karton.erfasst !== null && karton.erfasst !== karton.kartons && <div className="text-xs text-muted">erfasst: {kartonWort(karton.erfasst)}</div>}
                  </> : <span className="text-xs text-muted">nicht erfasst</span>}
                </td>}
                <td className="p-1.5 whitespace-nowrap">
                  {p.state === 'verschlossen' ? p.qtyExact === null ? 'unbekannt' : `${p.qtyExact} ${s.unit}`
                    // angebrochen und kartongenau fortgeschrieben (C.1) zählt exakt, wie „aus Restkartons gerechnet“ oben
                    : p.qtyCirca !== null ? `${p.state === 'ausgepackt' && karton ? '' : 'ca. '}${p.qtyCirca} ${s.unit}` : 'unbekannt'}
                </td>
                <td className="p-1.5">{p.firstInAt ? new Date(p.firstInAt).toLocaleDateString('de-DE') : '—'}</td>
                <td className="p-1.5 font-mono text-xs">{p.labelId ?? '—'}</td>
                {warehouse.lead && <td className="p-1.5"><LeiterPaletteInline position={p} unit={s.unit}
                  onSaved={() => { void q.refetch(); void qc.invalidateQueries({ queryKey: ['artikel-bewegungen'] }) }} />
                  <Button size="sm" variant="outline" onClick={() => setKorrektur(p)}>Korrigieren</Button></td>}
              </tr>
            ))}
            {/* Packstation als eigene Zeile unter den Paletten: wie viel Stück liegen schon griffbereit (nur Stückware) */}
            {!netze && (s.packstation || s.packstationHinweis) && (
              <tr data-testid="packstation-zeile" className="border-t border-dashed border-line-2 bg-panel-2">
                {vorrangSpalte && <td className="p-1.5" />}
                {packplatzSpalte && <td className="p-1.5" />}
                <td className="p-1.5 font-medium" colSpan={2}>Packstation</td>
                <td className="p-1.5 text-xs text-muted whitespace-nowrap">
                  {s.packstation?.aktuell && s.packstation.countedAt ? `gezählt ${new Date(s.packstation.countedAt).toLocaleDateString('de-DE')}` : null}
                </td>
                <td className="p-1.5" />
                <td className="p-1.5 whitespace-nowrap">
                  {s.packstation?.aktuell ? <>
                    <div className="font-medium">{s.packstation.physisch} {s.unit}</div>
                    {s.packstation.frei !== s.packstation.physisch && <div className="text-xs text-muted">frei {s.packstation.frei} {s.unit}</div>}
                  </> : <span className="text-xs text-muted">{s.packstationHinweis === 'noch_nicht_gezaehlt' ? 'noch nicht gezählt' : 'Zähler nicht aktuell'}</span>}
                </td>
                <td className="p-1.5" colSpan={2} />
                {warehouse.lead && <td className="p-1.5"><HandregalKorrekturFeld merchantSku={merchantSku}
                  onSaved={() => { void q.refetch(); void qc.invalidateQueries({ queryKey: ['artikel-bewegungen'] }) }} /></td>}
              </tr>
            )}
            {visiblePositions.length === 0 && <tr><td colSpan={spalten} className="p-3 text-muted">{d.positions.length === 0 ? 'Keine Lager-Positionen erfasst.' : 'Keine Paletten für diese Rollenlänge.'}</td></tr>}
          </tbody>
          {!netze && zeilen.length > 0 && (
            <tfoot><tr className="border-t-2 border-line-2">
              <td colSpan={spalten} className="p-1.5 font-medium">
                Summe: {zeilen.length} {zeilen.length === 1 ? 'Palette' : 'Paletten'}{summe.einzeln < zeilen.length && ` · ${kartonWort(summe.kartons)}`} · {summe.stueck} {s.unit}
                {summe.ohneKarton > 0 && <span className="font-normal text-muted"> · {summe.ohneKarton}× ohne Kartonangabe</span>}
                {summe.unbekannt > 0 && <span className="font-normal text-muted"> · {summe.unbekannt}× Menge unbekannt</span>}
              </td>
            </tr></tfoot>
          )}
        </table>
      </div>
      {korrektur && warehouse.lead && <LeiterPaletteKorrektur key={korrektur.id} position={korrektur} unit={s.unit}
        onClose={() => setKorrektur(null)} onSaved={() => { setKorrektur(null); void q.refetch(); void qc.invalidateQueries({ queryKey: ['artikel-bewegungen'] }) }} />}
      {warehouse.lead && (
        <div className="text-xs text-muted">
          Vorrang: <b className="font-medium text-ink-2">sofort</b> = ab der nächsten Anforderung von dieser Palette, auch wenn eine andere offen ist ·{' '}
          <b className="font-medium text-ink-2">als nächstes</b> = sobald die offene Palette leer ist. Je Artikel nur eine; bereits vergebene Picks bleiben.
        </div>
      )}
      {warehouse.book && (
        <div className="flex gap-2 items-center">
          <Input className="w-56" placeholder="Platz zuweisen (R26+F09+P1+EB1)" value={platz} onChange={(e) => setPlatz(e.target.value)} />
          <Button variant="outline" disabled={platz.length < 5 || assignBusy} onClick={() => void onAssign()}>
            {assignBusy ? 'Zuweise…' : 'Zuweisen (ohne Menge)'}
          </Button>
        </div>
      )}
    </div>
  )
}
